import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Phone, MapPin, Navigation, UserPlus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getRide, getDrivers, assignDriver, resolveRide, deleteRide } from '../../services/api';
import { isAdminUser } from '../../utils/adminUser';
import { formatRidePriceLek } from '../../utils/ridePrice';
import { getRideScheduledAt } from '../../utils/rideFormat';
import { getAdAttribution } from '../../utils/adSource';
import { useSettings } from '../../context/SettingsContext';

const haversineKm = (lat1, lng1, lat2, lng2) => {
  if ([lat1, lng1, lat2, lng2].some((v) => !Number.isFinite(v))) return Number.POSITIVE_INFINITY;
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.sqrt(a));
};

const AdminRideDetailsPage = () => {
  const navigate = useNavigate();
  const { rideId } = useParams();
  const { formatDateTime } = useSettings();

  const [ride, setRide] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigningDriverId, setAssigningDriverId] = useState(null);

  const fetchData = useCallback(async () => {
    if (!rideId) return;

    try {
      setLoading(true);
      const [rideData, driversData] = await Promise.all([
        (async () => {
          try {
            return await getRide(rideId);
          } catch (err) {
            const fallback = await api.get(`/airport/rides/${rideId}`);
            return { ...fallback.data, dispatch_type: 'airport' };
          }
        })(),
        getDrivers(),
      ]);
      setRide(rideData || null);
      setDrivers(driversData || []);
    } catch (error) {
      console.error('Error loading ride details:', error);
      toast.error(error?.response?.data?.detail || 'Nuk u ngarkuan detajet e udhetimit');
      setRide(null);
    } finally {
      setLoading(false);
    }
  }, [rideId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCancelRide = async (id) => {
    const reason = window.prompt('Arsyeja e anulimit (e detyrueshme):');
    if (!reason?.trim()) return;
    try {
      await resolveRide(id, { action: 'cancel', reason: reason.trim() });
      toast.success('Udhëtimi u anulua me sukses');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Anulimi dështoi');
    }
  };

  const handleCompleteRide = async (id) => {
    if (!window.confirm('Përfundoni këtë udhëtim manualisht?')) return;
    try {
      await resolveRide(id, {
        action: 'complete',
        amountReceived: ride?.final_price || ride?.estimated_price || ride?.price_eur,
      });
      toast.success('Udhëtimi u përfunduar');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Përfundimi dështoi');
    }
  };

  const isActiveRide = ['in_progress', 'arriving', 'accepted', 'arrived_pickup', 'assigned', 'en_route', 'passenger_onboard', 'at_stopover'].includes(ride?.status);

  const adminCanDelete = isAdminUser();

  const handleDeleteRide = async () => {
    if (!ride?.id) return;
    const label = ride.ride_number || `#${String(ride.id).slice(0, 8)}`;
    if (
      !window.confirm(
        `Fshini përgjithmonë porosinë ${label}?\n\nKy veprim nuk mund të kthehet mbrapsht.`
      )
    ) {
      return;
    }
    try {
      await deleteRide(ride.id);
      toast.success('Porosia u fshi');
      navigate('/admin/rides');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fshirja dështoi');
    }
  };

  const handleAssignDriver = async (driverId) => {
    if (!ride?.id || !driverId) return;
    try {
      setAssigningDriverId(driverId);
      if (ride?.dispatch_type === 'airport') {
        await api.put(`/airport/rides/${ride.id}/assign`, { driver_id: driverId });
      } else {
        await assignDriver(ride.id, driverId);
      }
      toast.success('Shoferi u caktua me sukses');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Caktimi dështoi');
    } finally {
      setAssigningDriverId(null);
    }
  };

  const pickupLat = ride?.pickup?.position?.lat;
  const pickupLng = ride?.pickup?.position?.lng;
  const onlineDriversNearest = (drivers || [])
    .filter((d) => d?.status === 'online' && d?.position)
    .map((d) => ({
      ...d,
      distanceKm: haversineKm(pickupLat, pickupLng, d.position?.lat, d.position?.lng),
    }))
    .filter((d) => Number.isFinite(d.distanceKm))
    .sort((a, b) => a.distanceKm - b.distanceKm);

  const closestDriver = onlineDriversNearest[0] || null;

  if (loading) {
    return (
      <div className="h-[70vh] flex items-center justify-center">
        <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (!ride) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/admin/rides')}
            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
          >
            <ArrowLeft className="w-4 h-4" />
            Kthehu te porositë
          </button>
        </div>
        <p className="text-gray-600">Udhëtimi nuk u gjet.</p>
      </div>
    );
  }

  const adSource = getAdAttribution(ride);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden max-w-3xl">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <button
          onClick={() => navigate('/admin/rides')}
          className="inline-flex items-center gap-2 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" />
          Kthehu te porositë
        </button>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="w-4 h-4" />
          Rifresko
        </button>
      </div>

      <div className="p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {ride.ride_number || `#${String(ride.id || '').slice(0, 8)}`}
            </h2>
            <p className="text-sm text-gray-500">
              Statusi: <span className="font-medium text-gray-700">{ride.status || 'pending'}</span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            {!['completed', 'cancelled'].includes(ride.status) ? (
              <>
                {isActiveRide && (
                  <button
                    onClick={() => handleCompleteRide(ride.id)}
                    className="px-3 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700"
                  >
                    Përfundo
                  </button>
                )}
                <button
                  onClick={() => handleCancelRide(ride.id)}
                  className="px-3 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                >
                  Anulo
                </button>
              </>
            ) : null}
            {adminCanDelete ? (
              <button
                onClick={handleDeleteRide}
                className="px-3 py-2 text-sm rounded-lg border border-red-500 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5"
                data-testid="admin-delete-ride"
              >
                <Trash2 className="w-4 h-4" />
                Fshi
              </button>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-100 p-4">
              <p className="text-xs uppercase text-gray-500 mb-2">Pasagjeri</p>
              <p className="font-semibold text-gray-900">{ride.passenger_name || 'N/A'}</p>
              <p className="text-sm text-gray-600">{ride.passenger_phone || 'N/A'}</p>
              {adSource ? (
                <p className="mt-2">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${adSource.className}`}>
                    {adSource.text}
                  </span>
                </p>
              ) : null}
              {ride.passenger_phone ? (
                <a
                  href={`tel:${ride.passenger_phone}`}
                  className="inline-flex items-center gap-2 mt-3 text-sm text-green-700 hover:underline"
                >
                  <Phone className="w-4 h-4" />
                  Telefono
                </a>
              ) : null}
            </div>
            {ride.created_at && (
              <div className="rounded-lg border border-gray-100 p-4 text-sm">
                <p className="text-gray-500">Data/Ora e rezervimit</p>
                <p className="font-semibold text-gray-900">{formatDateTime(ride.created_at)}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-gray-100 p-4">
              <p className="text-xs uppercase text-gray-500 mb-2">Shoferi</p>
              <p className="font-semibold text-gray-900">{ride.driver_name || 'Pa caktim'}</p>
              <p className="text-sm text-gray-600">{ride.driver_phone || 'N/A'}</p>
              <p className="text-sm text-gray-600 mt-1">{ride.vehicle?.plate || ride.vehicle_plate || 'Targë N/A'}</p>
            </div>
            {getRideScheduledAt(ride) && (
              <div className="rounded-lg border border-gray-100 p-4 text-sm">
                <p className="text-gray-500">Data/Ora e udhetimit</p>
                <p className="font-semibold text-gray-900">{formatDateTime(getRideScheduledAt(ride))}</p>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 p-4 space-y-3">
          <p className="text-xs uppercase text-gray-500">Itinerari</p>

          <div className="flex items-start gap-2 text-sm text-gray-800">
            <MapPin className="w-4 h-4 text-green-600 mt-0.5" />
            <span>{ride.pickup?.address || 'Pickup N/A'}</span>
          </div>

          <div className="flex items-start gap-2 text-sm text-gray-800">
            <MapPin className="w-4 h-4 text-red-600 mt-0.5" />
            <span>{ride.dropoff?.address || ride.destination?.address || 'Dropoff N/A'}</span>
          </div>
        </div>

        <div className="rounded-lg border border-gray-100 p-4 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">Çmimi</p>
            <p className="font-semibold text-gray-900">{formatRidePriceLek(ride)}</p>
          </div>
          <div>
            <p className="text-gray-500">Pagesa</p>
            <p className="font-semibold text-gray-900">{ride.payment_method || 'cash'}</p>
          </div>
          <div>
            <p className="text-gray-500">Distanca</p>
            <p className="font-semibold text-gray-900">{ride.distance || ride.distance_km || 0} km</p>
          </div>
          <div>
            <p className="text-gray-500">Kohëzgjatja</p>
            <p className="font-semibold text-gray-900">{ride.estimated_duration || ride.duration_minutes || 0} min</p>
          </div>
        </div>

        {['pending', 'accepted', 'arriving'].includes(ride.status) && (
          <div className="rounded-lg border border-gray-100 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase text-gray-500">Cakto Shofer</p>
              <span className="text-xs text-gray-500">Online: {onlineDriversNearest.length}</span>
            </div>

            {closestDriver ? (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Më i afërti: {closestDriver.name}</p>
                  <p className="text-xs text-gray-600">
                    {closestDriver.vehicle?.plate || 'N/A'} • {closestDriver.distanceKm.toFixed(2)} km nga pickup
                  </p>
                </div>
                <button
                  onClick={() => handleAssignDriver(closestDriver.id)}
                  disabled={!!assigningDriverId}
                  className="px-3 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-300 inline-flex items-center gap-2"
                >
                  <Navigation className="w-4 h-4" />
                  Lidhe me më të afërtin
                </button>
              </div>
            ) : (
              <div className="text-sm text-gray-500">Nuk ka shoferë online me pozicion.</div>
            )}

            {onlineDriversNearest.length > 0 && (
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {onlineDriversNearest.slice(0, 8).map((driver) => (
                  <div key={driver.id} className="p-2 rounded-lg border border-gray-100 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{driver.name}</p>
                      <p className="text-xs text-gray-600">
                        {driver.phone || 'N/A'} • {driver.vehicle?.plate || 'N/A'} • {driver.distanceKm.toFixed(2)} km
                      </p>
                    </div>
                    <button
                      onClick={() => handleAssignDriver(driver.id)}
                      disabled={!!assigningDriverId}
                      className="px-2.5 py-1.5 text-xs rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-60 inline-flex items-center gap-1"
                    >
                      <UserPlus className="w-3.5 h-3.5" />
                      Cakto
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminRideDetailsPage;
